import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import styles from './SideDrawer.module.scss';
import { getText } from '../../i18n';
/**
 * @file SideDrawer.js
 * @description A side drawer component that slides in from the right to display content.
 * It accepts dynamic content such as a header and sections with labels and text
 *
 * @component
 *
 * @param {boolean} isOpen - boolean value that controls the visibility of the side drawer
 * @param {function} setIsOpen - function that sets the state of isOpen to control the visibility of the side drawer
 * @param {string} header - title/header of the side drawer, displayed at the top
 * @param {Array} sections - an array of objects representing the sections to be displayed within the drawer.
 * Each object should have the following structure: {label: string, text: string}
 * @param {function} [onContentClick] - optional click handler attached to the sections list, useful for
 * event delegation (e.g. detecting clicks on `<code>` elements within a section's text)
 */
const SideDrawer = ({
    isOpen,
    setIsOpen,
    header,
    sections,
    onContentClick,
}) => {
    const closeDrawer = () => {
        setIsOpen(false);
    };

    useEffect(() => {
        const handleKeyDown = event => {
            if (event.key === 'Escape') {
                closeDrawer();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen]);

    const drawerContent = (
        <>
            {isOpen && (
                <div
                    className={styles.sideDrawerOverlay}
                    onClick={closeDrawer}
                ></div>
            )}
            <div
                className={`${styles.sideDrawer} ${isOpen ? styles.open : ''}`}
            >
                <div className={styles.sideDrawerContent}>
                    <div className={styles.sideDrawerHeaderContainer}>
                        <div className={styles.sideDrawerHeader}>{header}</div>
                        <div
                            className={styles.sideDrawerCloseContainer}
                            onClick={closeDrawer}
                        >
                            <i
                                className={`bi bi-x me-2 pb-4 ${styles.sideDrawerCloseButton}`}
                            />
                            <div className={styles.sideDrawerCloseButtonText}>
                                {getText('msg_bbfa773e5a63')}
                            </div>
                        </div>
                    </div>
                    <ul onClick={onContentClick}>
                        {sections.map((section, index) => (
                            <li key={index}>
                                <div className={styles.sectionHeader}>
                                    {section.label}
                                </div>
                                {section.text}
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </>
    );

    return ReactDOM.createPortal(drawerContent, document.body);
};

export default SideDrawer;
